import React, { useCallback, useMemo, useState } from 'react'

import { Drawer } from 'antd'
import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import InternshipRequestFilesTable from './InternshipRequestFilesTable'
import useRequest from '../../../../hooks/useRequest'
import { selectUser } from '../../../../reducers/UserReducer'
import { deleteInternshipRequestFile, getInternshipRequestFiles } from '../../../../utils/api/internshipRequest'
import InternshipRequestFileModal from '../../../../Providers/Forms/InternshipRequestFileModal'
import { onError, onSuccess } from '../../../../utils/apiHelper'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import useScreenSize from '../../../../hooks/useScreen'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'

const DEFAULT_REQUEST = { data: null, callback: null }

export const InternshipRequestFilesDrawerContext = React.createContext()

const InternshipRequestFilesDrawer = ({ children }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const { width } = useScreenSize()

  const [handler, setHandler] = useState(DEFAULT_REQUEST)
  const [dirty, setDirty] = useState(false)
  const [parameters, setParameters] = useState(new TableParameters())

  const { data, loading, metadata, reload, setData } = useRequest({
    request: getInternshipRequestFiles,
    parameters: useMemo(() => ({ ...handler.data, parameters }), [parameters, handler.data]),
    enabled: !!handler.data
  })

  const handleDelete = useCallback(file => {
    deleteInternshipRequestFile(user, handler.data, file).then(json => {
      if (json?.data) {
        setData(data.filter(f => f.id !== json.data.id))
        setDirty(true)
        onSuccess(t('internship_request_file.delete.success'))
      } else {
        onError(t('internship_request_file.delete.error'))
      }
    })
  }, [handler.data, user, data, setData, setDirty, t])

  const handleClose = useCallback(() => {
    if (dirty) {
      if (isFunction(handler.callback)) {
        handler.callback()
      }

      setDirty(false)
    }

    setHandler(DEFAULT_REQUEST)
  }, [setHandler, setDirty, handler.callback, dirty])

  return (
    <>
      <InternshipRequestFilesDrawerContext.Provider value={{ setHandler }}>
        {children}
      </InternshipRequestFilesDrawerContext.Provider>
      <Drawer
        title={t('internship_requests_table.actions.consult_files')}
        width={width > 720 ? 720 : '100%'}
        onClose={handleClose}
        visible={!!handler.data}
      >
        <InternshipRequestFileModal>
          <InternshipRequestFilesTable
            data={data}
            institution={user.institutions[0]}
            individualInternshipRequest={handler.data}
            loading={loading}
            metadata={metadata}
            parameters={parameters}
            onDelete={handleDelete}
            onParametersChange={setParameters}
            reload={() => {
              reload()
              setDirty(true)
            }}
          />
        </InternshipRequestFileModal>
      </Drawer>
    </>
  )
}

export default InternshipRequestFilesDrawer
